import { Button } from "@gigpro/ui/Button";
import { Checkbox } from "@gigpro/ui/Checkbox";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form, type SubmitHandler } from "@gigpro/ui/Form";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { requiredStringSchema, useZodForm } from "@gigpro/ui/Form/utils";
import { Delete, Plus } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { ToggleGroup, ToggleGroupItem, ToggleGroupItemUnstyled } from "@gigpro/ui/ToggleGroup";
import { ApiError } from "@sidegigllc/api";
import type { ColorSchemeEnum, SectionEnum, UniformCatalogItem } from "@sidegigllc/api/client";
import { COLOR_SCHEME_LABELS, COLOR_SCHEMES, SECTION_ORDER, UNIFORM_SECTION_LABELS } from "api/resources/uniforms";
import React from "react";
import * as z from "zod/mini";

/** Controlled value for the custom builder. `itemIds` reference garments in the union catalog. */
export interface CustomUniformValue {
  colorScheme: ColorSchemeEnum;
  itemIds: Array<number>;
}

interface CustomUniformBuilderProps {
  /**
   * Garment catalog to render, grouped by section — the union of the scheme-scoped global catalog
   * (CORE-6275) and this location's own custom garments (CORE-6244), assembled by the container.
   */
  items: Array<UniformCatalogItem>;
  value: CustomUniformValue;
  onChange: (value: CustomUniformValue) => void;
  /** Ids of location-owned custom garments — these render a delete (trash) affordance. */
  deletableItemIds: ReadonlySet<number>;
  /** Persist a new location custom garment. Rejects with an `ApiError` on validation failure. */
  onAddCustom: (section: SectionEnum, label: string) => Promise<void>;
  /** Remove a location custom garment. */
  onRemoveCustom: (id: number) => void;
  className?: string;
}

/**
 * W3 — Custom uniform builder. A Black-on-Black / White-on-Black color toggle plus garment
 * checkboxes grouped by section (Top / Bottom / Footwear / Other) and an "Add your own" per section.
 * Presentational only: the union catalog comes in via props, selection out via `onChange`, and
 * add/remove of location custom garments bubble up through `onAddCustom` / `onRemoveCustom` — the
 * container owns the location uniform-items API calls (CORE-6244).
 */
export function CustomUniformBuilder({
  items,
  value,
  onChange,
  deletableItemIds,
  onAddCustom,
  onRemoveCustom,
  className,
}: Readonly<CustomUniformBuilderProps>) {
  const id = React.useId();
  const [addSection, setAddSection] = React.useState<false | SectionEnum>(false);

  const setColor = (colorScheme: ColorSchemeEnum) => onChange({ ...value, colorScheme });
  const setItemIds = (ids: Array<string>) => onChange({ ...value, itemIds: ids.map(Number) });

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <ToggleGroup
        type="single"
        value={value.colorScheme}
        onValueChange={(next) => next && setColor(next as ColorSchemeEnum)}
        className="w-full"
      >
        {COLOR_SCHEMES.map((scheme) => (
          <ToggleGroupItem key={scheme} value={scheme}>
            {COLOR_SCHEME_LABELS[scheme]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <ToggleGroup
        type="multiple"
        value={value.itemIds.map(String)}
        onValueChange={setItemIds}
        className="flex flex-col gap-1 bg-transparent"
      >
        {SECTION_ORDER.map((section) => (
          <React.Fragment key={section}>
            <h3 className="mt-2 text-large font-semibold first:mt-0">{UNIFORM_SECTION_LABELS[section]}</h3>
            <div className="grid gap-2 rounded-sm bg-background p-3 sm:grid-cols-2">
              {items
                .filter((item) => item.section === section)
                .map((item) => (
                  <GarmentCheckbox
                    key={item.id}
                    id={`${id}-item-${item.id}`}
                    value={item.id}
                    label={item.label}
                    checked={value.itemIds.includes(item.id)}
                    onRemove={deletableItemIds.has(item.id) ? () => onRemoveCustom(item.id) : undefined}
                  />
                ))}
              <AddCustom onClick={() => setAddSection(section)} />
            </div>
          </React.Fragment>
        ))}
      </ToggleGroup>

      <NewGarmentDialog
        open={!!addSection}
        onOpenChange={(open) => setAddSection((prev) => (open ? prev : false))}
        section={typeof addSection === "string" ? addSection : undefined}
        onAddCustom={onAddCustom}
      />
    </div>
  );
}

function GarmentCheckbox({
  id,
  value,
  label,
  checked,
  onRemove,
}: Readonly<{ id: string; value: number; label: string; checked: boolean; onRemove?: () => void }>) {
  return (
    <span className="flex w-full items-center gap-2">
      <ToggleGroupItemUnstyled
        asChild
        id={id}
        value={`${value}`}
        className="data-[state=on]:border-brand data-[state=on]:bg-brand"
      >
        <Checkbox checked={checked} />
      </ToggleGroupItemUnstyled>
      <label htmlFor={id} className="flex-1 cursor-pointer text-normal">
        {label}
      </label>
      {onRemove && (
        <Button type="button" size="small" className="p-0" onClick={onRemove} aria-label={`Remove ${label}`}>
          <Delete className="size-4" />
        </Button>
      )}
    </span>
  );
}

function AddCustom({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <Button type="button" size="small" className="col-span-full mt-2 justify-start p-0" onClick={onClick}>
      <Plus className="mr-2 size-4" /> Add your own
    </Button>
  );
}

const NewGarment = z.object({ label: requiredStringSchema });
type NewGarment = z.infer<typeof NewGarment>;
const newGarmentFormId = "new-garment-form";

interface NewGarmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section?: SectionEnum;
  onAddCustom: (section: SectionEnum, label: string) => Promise<void>;
}

function NewGarmentDialog({ open, onOpenChange, section, onAddCustom }: Readonly<NewGarmentDialogProps>) {
  const form = useZodForm({ schema: NewGarment, values: { label: "" } });
  React.useEffect(() => {
    if (open) form.reset({ label: "" });
  }, [form, open]);

  const onSubmit: SubmitHandler<NewGarment> = async (values, event) => {
    event?.stopPropagation();
    if (!section) return;
    try {
      await onAddCustom(section, values.label);
      onOpenChange(false);
    } catch (error) {
      ApiError.fromThrown(error).setFormErrors(form.setError);
    }
  };

  const sectionLabel = section ? UNIFORM_SECTION_LABELS[section] : "garment";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-md">
      <DialogTitle>Add {sectionLabel} garment</DialogTitle>
      <Form id={newGarmentFormId} form={form} onSubmit={onSubmit}>
        <Textfield
          control={form.control}
          name="label"
          label="Name"
          placeholder="Enter garment name..."
          autoComplete="off"
          maxLength={40}
        />
      </Form>
      <DialogFooter>
        <Button size="small" onClick={() => onOpenChange(false)} disabled={form.formState.isSubmitting}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          type="submit"
          loading={form.formState.isSubmitting}
          form={newGarmentFormId}
          onClick={(e) => {
            // prevent the outer dialog from closing itself (mirrors NewRequirementDialog)
            e.preventDefault();
            void form.handleSubmit(onSubmit)(e);
          }}
        >
          Add
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
